import { FC, useMemo, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Divider,
    Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { createSubCompanyThunk, deleteSubCompanyThunk, editSubCompaniesThunk } from "../../store/thunk/subCompaniesThunk";
import { CompanyType } from "../../types/companyManagementTypes";
import { usePermissionChecker } from "../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../types/permissionType";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../common/ui/GeneralButton";

interface AddSubCompanyDialogProps {
    open: boolean;
    onClose: () => void;
    initialData?: Partial<CompanyType>;
    mode?: "add" | "edit";
    onSuccess?: (newSubcompany: CompanyType) => void;
}

interface FormDataState {
    name: string;
    phoneNumber: string;
    taxId: string;
    invoiceEmail: string;
    street: string;
    number: string;
    extraAddressLine: string;
    postalCode: string;
    town: string;
    country: string;
    canBeProcessed: boolean;
}

const FormGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(3),
    gridTemplateColumns: "1fr 1fr",
    [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr"
    }
}));

const FormSection = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(4)
}));

type DisplayField = Exclude<keyof FormDataState, "canBeProcessed">;

const AddOrEditSubCompanyDialog: FC<AddSubCompanyDialogProps> = ({
    open,
    onClose,
    initialData,
    mode = 'add',
    onSuccess
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { userHasPermission } = usePermissionChecker();
    const canDeleteSubCompany =
        !!initialData?.id &&
        (userHasPermission(PermissionEntityType.SubCompanies, "Delete") ||
            userHasPermission(PermissionEntityType.SubCompanies, "Delete", initialData.id));

    const [formData, setFormData] = useState({
        name: "",
        phoneNumber: "",
        taxId: "",
        invoiceEmail: "",
        street: "",
        number: "",
        extraAddressLine: "",
        postalCode: "",
        town: "",
        country: "",
        canBeProcessed: false,
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            name: initialData?.name ?? "",
            phoneNumber: initialData?.phoneNumber ?? "",
            taxId: initialData?.taxId ?? "",
            invoiceEmail: initialData?.invoiceEmail ?? "",
            street: initialData?.address?.street ?? "",
            number: initialData?.address?.number ?? "",
            extraAddressLine: initialData?.address?.extraAddressLine ?? "",
            postalCode: initialData?.address?.postalCode ?? "",
            town: initialData?.address?.town ?? "",
            country: initialData?.address?.country ?? "",
            canBeProcessed: initialData?.address?.canBeProcessed ?? false,
        }));
    }, [initialData]);

    const requiredFields = [
        "name",
        "phoneNumber",
        "taxId",
        "invoiceEmail",
        "street",
        "number",
        "postalCode",
        "town",
        "country",
    ] as const satisfies readonly DisplayField[];

    const fieldLabels: Record<DisplayField, string> = {
        name: t("companyManagementPage.companyInfoTable.company"),
        phoneNumber: t("companyManagementPage.companyInfoTable.phoneNumber"),
        taxId: t("companyManagementPage.companyInfoTable.taxId"),
        invoiceEmail: t("companyManagementPage.companyInfoTable.invoiceEmail"),
        street: t("companyManagementPage.companyInfoTable.street"),
        number: t("companyManagementPage.companyInfoTable.number"),
        extraAddressLine: t("companyManagementPage.companyInfoTable.extraAddressLine"),
        postalCode: t("companyManagementPage.companyInfoTable.postalCode"),
        town: t("companyManagementPage.companyInfoTable.city"),
        country: t("companyManagementPage.companyInfoTable.country"),
    };

    const handleChange = <K extends keyof FormDataState>(
        field: K,
        value: FormDataState[K]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const missingFields = useMemo(() => {
        return requiredFields.filter(field => !formData[field]?.toString().trim());
    }, [formData, requiredFields]);

    const handleSubmit = async () => {
        const payload = {
            id: (initialData as any)?.id,
            subCompanyData: {
                name: formData.name,
                phoneNumber: formData.phoneNumber,
                taxId: formData.taxId,
                invoiceEmail: formData.invoiceEmail,
                address: {
                    street: formData.street,
                    number: formData.number,
                    extraAddressLine: formData.extraAddressLine,
                    postalCode: formData.postalCode,
                    town: formData.town,
                    country: formData.country,
                    canBeProcessed: true
                }
            }
        };

        try {
            if (mode === "add") {
                const newSubcompany = await dispatch(createSubCompanyThunk(payload.subCompanyData)).unwrap();
                if (newSubcompany && onSuccess) {
                    onSuccess(newSubcompany);
                }
            } else {
                const updatedSubcompany = await dispatch(editSubCompaniesThunk(payload)).unwrap();
                if (updatedSubcompany && onSuccess) {
                    onSuccess(updatedSubcompany);
                }
            }
            onClose();
        } catch (error) {
            console.error(
                mode === "add"
                    ? t("companyManagementPage.subCompany.createFailed")
                    : t("companyManagementPage.subCompany.editFailed"),
                error
            );
        }
    };

    const handleDelete = async () => {
        if (!initialData?.id) return;
        const confirmed = window.confirm(
            t("companyManagementPage.subCompany.confirmDelete")
        )
        if (!confirmed) return;

        try {
            await dispatch(deleteSubCompanyThunk(initialData.id)).unwrap();
            onClose();
        } catch (error) {
            console.error(
                t("companyManagementPage.subCompany.deleteFailed"),
                error
            );
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {mode === "add"
                    ? t("companyManagementPage.subCompany.addTitle")
                    : t("companyManagementPage.subCompany.editTitle")}
            </DialogTitle>
            <DialogContent sx={{ pb: 4, pt: 2 }}>
                <FormSection>
                    <Divider sx={{ my: 3 }}>{t("companyManagementPage.companyInfoTable.company")}</Divider>
                    <FormGrid>
                        <TextField label={t("companyManagementPage.companyInfoTable.company")} value={formData.name} onChange={e => handleChange("name", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.phoneNumber")} value={formData.phoneNumber} onChange={e => handleChange("phoneNumber", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.taxId")} value={formData.taxId} onChange={e => handleChange("taxId", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.invoiceEmail")} type="email" value={formData.invoiceEmail} onChange={e => handleChange("invoiceEmail", e.target.value)} required />
                    </FormGrid>
                </FormSection>

                <FormSection>
                    <Divider sx={{ my: 3 }}>{t("companyManagementPage.companyInfoTable.billAddress")}</Divider>
                    <FormGrid>
                        <TextField label={t("companyManagementPage.companyInfoTable.street")} value={formData.street} onChange={e => handleChange("street", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.number")} value={formData.number} onChange={e => handleChange("number", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.extraAddressLine")} value={formData.extraAddressLine} onChange={e => handleChange("extraAddressLine", e.target.value)} />
                        <TextField label={t("companyManagementPage.companyInfoTable.postalCode")} value={formData.postalCode} onChange={e => handleChange("postalCode", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.city")} value={formData.town} onChange={e => handleChange("town", e.target.value)} required />
                        <TextField label={t("companyManagementPage.companyInfoTable.country")} value={formData.country} onChange={e => handleChange("country", e.target.value)} required />
                    </FormGrid>
                </FormSection>
            </DialogContent>
            <DialogActions>
                {canDeleteSubCompany && (
                    <GeneralButton
                        appVariant="danger"
                        sx={{ mr: "auto" }}
                        onClick={handleDelete}
                    >
                        {t("buttons.delete")}
                    </GeneralButton>
                )}
                <GeneralButton
                    appVariant="secondary"
                    onClick={onClose}
                >
                    {t("buttons.cancel")}
                </GeneralButton>

                <Tooltip
                    title={
                        missingFields.length > 0
                            ? `${t("authentication.register.missingFields")}: ${missingFields
                                .map(f => fieldLabels[f])
                                .join(", ")}`
                            : ""
                    }
                    disableInteractive
                >
                    <span>
                        <GeneralButton
                            appVariant="primary"
                            onClick={handleSubmit}
                            disabled={missingFields.length > 0}
                        >
                            {t("buttons.ok")}
                        </GeneralButton>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default AddOrEditSubCompanyDialog;
